import './App.css'
import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  Bell,
  Building2,
  CalendarDays,
  CheckSquare,
  ChevronRight,
  Command,
  Contact2,
  Download,
  FileText,
  Hammer,
  LayoutDashboard,
  LineChart as LineChartIcon,
  Moon,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Receipt,
  Search,
  Sparkles,
  Sun,
  Users,
  Calculator,
  Key,
  LayoutList,
  Mail,
  Zap,
  GitBranch,
} from 'lucide-react'
import AccountingModule from './modules/AccountingModule'
import LeasingModule from './modules/LeasingModule'
import KanbanModule from './modules/KanbanModule'
import InboxModule from './modules/InboxModule'
import AutomationModule from './modules/AutomationModule'
import LifecycleModule from './modules/LifecycleModule'
import ConstructionModule from './modules/ConstructionModule'
import DealAnalyzer from './modules/DealAnalyzer'
import DealPipelineModule from './modules/DealPipelineModule'
import InvestorOSModule from './modules/InvestorOSModule'
import OperatorDashboardModule from './modules/OperatorDashboardModule'
import PropertiesPipelineModule from './modules/PropertiesPipelineModule'
import DemoOne from './components/ui/demo'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Badge } from './components/ui/badge'
import { Button } from './components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card'
import { Input } from './components/ui/input'
import { readWorkspaceForUser, writeWorkspaceForUser, type OperatorWorkspace } from './lib/operatorWorkspace'
import { cn } from './lib/utils'
import { completeTutorial, loginAuthProfile, signupAuthProfile } from './lib/authApi'

type RoleType = 'tenant' | 'realtor' | 'landlord'
type RealtorTrack = 'realtor' | 'rehab-specialist' | 'property-manager'
type Theme = 'light' | 'dark'
type ModuleId =
  | 'dashboard'
  | 'properties'
  | 'tenants'
  | 'transactions'
  | 'brrr-projects'
  | 'deals-pipeline'
  | 'contacts'
  | 'documents'
  | 'tasks'
  | 'calendar'
  | 'notifications'
  | 'accounting'
  | 'leasing'
  | 'work-order-board'
  | 'inbox'
  | 'automation'
  | 'lifecycle'
  | 'construction'
  | 'deal-analyzer'
  | 'investor-os'
  | 'ui-overhaul'

type AuthUser = {
  id: string
  name: string
  email: string
  username: string
  role: RoleType
  tutorialCompleted: boolean
  isDemo: boolean
  sessionToken?: string
}
type RowStatus = 'Active' | 'Pending' | 'At Risk' | 'Closed'

type KpiItem = {
  label: string
  value: string
  delta: string
  tone?: 'profit' | 'loss'
}

type TrendPoint = {
  month: string
  income: number
  expenses: number
  cashflow: number
  occupancy: number
  value: number
  maintenance: number
  collected: number
  pipeline: number
  commission: number
}

type PieSlice = {
  name: string
  value: number
  color: string
}

type CalendarItem = {
  time: string
  title: string
}

type RoleDashboardConfig = {
  heading: string
  subheading: string
  kpis: KpiItem[]
  trendData: TrendPoint[]
  pieData: PieSlice[]
  activity: string[]
  calendar: CalendarItem[]
}

type DataRow = {
  id: string
  primary: string
  secondary: string
  status: RowStatus
  value: number
  updatedAt: string
}

type ModuleConfig = {
  id: ModuleId
  label: string
  icon: ComponentType<{ className?: string }>
  description: string
  primaryLabel: string
  secondaryLabel: string
  valueLabel: string
  newActionLabel: string
  roles: RoleType[]
  realtorTracks?: RealtorTrack[]
  rows: DataRow[]
}

const AUTH_USER_STORAGE_KEY = 'realassist_auth_user'
const AUTH_SESSION_STORAGE_KEY = 'realassist_auth_session_token'
const THEME_STORAGE_KEY = 'realassist_theme'
const REALTOR_TRACK_STORAGE_KEY = 'realassist_realtor_track'

const REALTOR_TRACK_LABELS: Record<RealtorTrack | 'all', string> = {
  all: 'All Ops',
  realtor: 'Realtor',
  'rehab-specialist': 'Rehab Specialist',
  'property-manager': 'Property Manager',
}

const MOBILE_PRIMARY_MODULE_ORDER: ModuleId[] = ['dashboard', 'properties', 'construction', 'inbox', 'tasks']

const MIXED_OPERATOR_MODE = true

const UNIFIED_MODULE_ORDER: ModuleId[] = [
  'investor-os',
  'dashboard',
  'deal-analyzer',
  'deals-pipeline',
  'properties',
  'construction',
  'accounting',
  'leasing',
  'documents',
  'inbox',
  'calendar',
  'tasks',
  'contacts',
  'automation',
  'lifecycle',
  'notifications',
]

const MODULES: ModuleConfig[] = [
  {
    id: 'investor-os',
    label: 'Investor OS',
    icon: Sparkles,
    description: 'Unified operating system for acquisitions, rehab, rentals, finance, and ops.',
    primaryLabel: 'Capability',
    secondaryLabel: 'Lifecycle',
    valueLabel: 'Status',
    newActionLabel: 'Open Workspace',
    roles: ['realtor', 'landlord'],
    rows: [],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Portfolio command center for operations and investing performance.',
    primaryLabel: 'Item',
    secondaryLabel: 'Context',
    valueLabel: 'Value',
    newActionLabel: 'Add Widget',
    roles: ['tenant', 'realtor', 'landlord'],
    rows: [],
  },
  {
    id: 'properties',
    label: 'Properties',
    icon: Building2,
    description: 'Track portfolio assets, occupancy, and asset-level performance.',
    primaryLabel: 'Property',
    secondaryLabel: 'Manager',
    valueLabel: 'Asset Value',
    newActionLabel: 'Add Property',
    roles: ['tenant', 'landlord', 'realtor'],
    realtorTracks: ['realtor', 'property-manager'],
    rows: [
      { id: 'prop-1', primary: 'Maple Grove House', secondary: 'A. Rivera', status: 'Active', value: 220000, updatedAt: '2h ago' },
      { id: 'prop-2', primary: 'Willow Creek House', secondary: 'J. Brooks', status: 'Pending', value: 214000, updatedAt: '4h ago' },
      { id: 'prop-3', primary: 'Cedar Ridge House', secondary: 'S. Patel', status: 'At Risk', value: 229000, updatedAt: '1d ago' },
    ],
  },
  {
    id: 'tenants',
    label: 'Tenants',
    icon: Users,
    description: 'Monitor tenant health, payment status, and communication quality.',
    primaryLabel: 'Tenant',
    secondaryLabel: 'Unit',
    valueLabel: 'Monthly Rent',
    newActionLabel: 'Add Tenant',
    roles: ['tenant', 'realtor', 'landlord'],
    realtorTracks: ['property-manager'],
    rows: [
      { id: 'ten-1', primary: 'Jordan Blake', secondary: 'Apt 4B', status: 'Active', value: 2850, updatedAt: '30m ago' },
      { id: 'ten-2', primary: 'Avery Kim', secondary: 'Apt 12C', status: 'Pending', value: 3100, updatedAt: '2h ago' },
      { id: 'ten-3', primary: 'Sam Porter', secondary: 'Unit 8', status: 'At Risk', value: 2400, updatedAt: '1d ago' },
      { id: 'ten-4', primary: 'Riley Chen', secondary: 'Townhome 6', status: 'Closed', value: 2650, updatedAt: '4d ago' },
    ],
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: Receipt,
    description: 'Track purchase/sale flows, escrow milestones, and settlement timing.',
    primaryLabel: 'Transaction',
    secondaryLabel: 'Counterparty',
    valueLabel: 'Deal Value',
    newActionLabel: 'New Transaction',
    roles: ['realtor', 'landlord'],
    realtorTracks: ['realtor'],
    rows: [
      { id: 't-1', primary: '18th Street Duplex Acquisition', secondary: 'Carter Holdings', status: 'Active', value: 1180000, updatedAt: '45m ago' },
      { id: 't-2', primary: 'Harbor Condo Exit', secondary: 'Westline Realty', status: 'Pending', value: 810000, updatedAt: '2h ago' },
      { id: 't-3', primary: 'Oakview Refinance', secondary: 'Prosper Lending', status: 'At Risk', value: 2400000, updatedAt: '20h ago' },
      { id: 't-4', primary: 'Cedar Duplex Disposition', secondary: 'Northpoint Group', status: 'Closed', value: 795000, updatedAt: '3d ago' },
    ],
  },
  {
    id: 'brrr-projects',
    label: 'BRRR Projects',
    icon: LineChartIcon,
    description: 'Analyze BRRR cycles from buy and rehab through refinance and rent.',
    primaryLabel: 'BRRR Deal',
    secondaryLabel: 'Phase Owner',
    valueLabel: 'Projected Equity',
    newActionLabel: 'Start BRRR',
    roles: ['realtor', 'landlord'],
    realtorTracks: ['realtor', 'rehab-specialist'],
    rows: [
      { id: 'b-1', primary: 'Sunset Triplex BRRR', secondary: 'Investment Team', status: 'Active', value: 210000, updatedAt: '1h ago' },
      { id: 'b-2', primary: 'Civic Quad BRRR', secondary: 'Finance Desk', status: 'Pending', value: 175000, updatedAt: '4h ago' },
      { id: 'b-3', primary: 'Bayline BRRR', secondary: 'Ops Team', status: 'At Risk', value: 95000, updatedAt: '1d ago' },
      { id: 'b-4', primary: 'Grandview BRRR', secondary: 'Portfolio Team', status: 'Closed', value: 260000, updatedAt: '6d ago' },
    ],
  },
  {
    id: 'deals-pipeline',
    label: 'Deals Pipeline',
    icon: LineChartIcon,
    description: 'Manage realtor and investment opportunities by stage and confidence.',
    primaryLabel: 'Deal',
    secondaryLabel: 'Stage Owner',
    valueLabel: 'Expected Value',
    newActionLabel: 'New Deal',
    roles: ['realtor', 'landlord'],
    realtorTracks: ['realtor'],
    rows: [
      { id: 'd-1', primary: 'Midtown Townhomes Buyer Side', secondary: 'N. Clark', status: 'Active', value: 1540000, updatedAt: '20m ago' },
      { id: 'd-2', primary: 'Lakepoint Seller Listing', secondary: 'A. Rivera', status: 'Pending', value: 980000, updatedAt: '2h ago' },
      { id: 'd-3', primary: 'Riverbend Mixed Use', secondary: 'T. Allen', status: 'At Risk', value: 2760000, updatedAt: '1d ago' },
      { id: 'd-4', primary: 'Broadway Duplex Offer', secondary: 'J. Brooks', status: 'Closed', value: 640000, updatedAt: '5d ago' },
    ],
  },
  {
    id: 'contacts',
    label: 'Contacts (CRM)',
    icon: Contact2,
    description: 'Unified CRM for owners, tenants, buyers, sellers, and vendors.',
    primaryLabel: 'Contact',
    secondaryLabel: 'Account',
    valueLabel: 'LTV',
    newActionLabel: 'Add Contact',
    roles: ['realtor', 'landlord'],
    rows: [
      { id: 'c-1', primary: 'Jordan Blake', secondary: 'Tenant - Riverstone', status: 'Active', value: 42000, updatedAt: '35m ago' },
      { id: 'c-2', primary: 'Carter Holdings', secondary: 'Investor Account', status: 'Pending', value: 185000, updatedAt: '2h ago' },
      { id: 'c-3', primary: 'Northstar HVAC', secondary: 'Vendor Account', status: 'At Risk', value: 15000, updatedAt: '1d ago' },
      { id: 'c-4', primary: 'Westline Realty', secondary: 'Brokerage Partner', status: 'Closed', value: 64000, updatedAt: '4d ago' },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: FileText,
    description: 'Centralized leases, contracts, disclosures, and financial docs.',
    primaryLabel: 'Document',
    secondaryLabel: 'Folder',
    valueLabel: 'Size (KB)',
    newActionLabel: 'Upload Document',
    roles: ['tenant', 'realtor', 'landlord'],
    rows: [
      { id: 'doc-1', primary: 'Lease - Riverstone Apt 4B', secondary: 'Leases', status: 'Active', value: 512, updatedAt: '18m ago' },
      { id: 'doc-2', primary: 'Buyer Representation Agreement', secondary: 'Contracts', status: 'Pending', value: 296, updatedAt: '3h ago' },
      { id: 'doc-3', primary: 'Repair Invoice - HVAC', secondary: 'Invoices', status: 'At Risk', value: 188, updatedAt: '1d ago' },
      { id: 'doc-4', primary: 'Closing Statement - Cedar Duplex', secondary: 'Closings', status: 'Closed', value: 724, updatedAt: '6d ago' },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: CheckSquare,
    description: 'Operational task queue across leasing, deals, and maintenance.',
    primaryLabel: 'Task',
    secondaryLabel: 'Assignee',
    valueLabel: 'Priority Score',
    newActionLabel: 'Create Task',
    roles: ['tenant', 'realtor', 'landlord'],
    rows: [
      { id: 'task-1', primary: 'Confirm Tuesday inspections', secondary: 'Ops Team', status: 'Active', value: 92, updatedAt: '12m ago' },
      { id: 'task-2', primary: 'Send updated rent notices', secondary: 'Leasing Team', status: 'Pending', value: 78, updatedAt: '2h ago' },
      { id: 'task-3', primary: 'Escrow timeline check', secondary: 'Transaction Desk', status: 'At Risk', value: 85, updatedAt: '1d ago' },
      { id: 'task-4', primary: 'Publish monthly owner report', secondary: 'Finance Team', status: 'Closed', value: 70, updatedAt: '2d ago' },
    ],
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: CalendarDays,
    description: 'Schedule-centric planning for tours, inspections, and closings.',
    primaryLabel: 'Event',
    secondaryLabel: 'Owner',
    valueLabel: 'Duration (min)',
    newActionLabel: 'New Event',
    roles: ['tenant', 'realtor', 'landlord'],
    rows: [
      { id: 'cal-1', primary: 'Tour - 35 Harbor Way', secondary: 'R. Sutton', status: 'Active', value: 45, updatedAt: '20m ago' },
      { id: 'cal-2', primary: 'Lease Renewal Review', secondary: 'A. Rivera', status: 'Pending', value: 30, updatedAt: '3h ago' },
      { id: 'cal-3', primary: 'Vendor Bid Walkthrough', secondary: 'Ops Team', status: 'At Risk', value: 60, updatedAt: '1d ago' },
      { id: 'cal-4', primary: 'Closing Signature Session', secondary: 'J. Lane', status: 'Closed', value: 50, updatedAt: '4d ago' },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    description: 'Unified alert center for urgent updates and operational follow-ups.',
    primaryLabel: 'Notification',
    secondaryLabel: 'Source',
    valueLabel: 'Priority',
    newActionLabel: 'Create Alert',
    roles: ['tenant', 'realtor', 'landlord'],
    rows: [
      { id: 'n-1', primary: '2 rent payments overdue', secondary: 'Collections', status: 'Active', value: 95, updatedAt: '5m ago' },
      { id: 'n-2', primary: 'Inspection reminder for Midtown', secondary: 'Calendar', status: 'Pending', value: 76, updatedAt: '1h ago' },
      { id: 'n-3', primary: 'Escrow deadline approaching', secondary: 'Transactions', status: 'At Risk', value: 90, updatedAt: '8h ago' },
      { id: 'n-4', primary: 'Portfolio report delivered', secondary: 'Reports', status: 'Closed', value: 66, updatedAt: '2d ago' },
    ],
  },
  {
    id: 'construction',
    label: 'Construction',
    icon: Hammer,
    description: 'Full contractor flow: estimate → approve → schedule → pay.',
    primaryLabel: 'Project',
    secondaryLabel: 'GC',
    valueLabel: 'Budget',
    newActionLabel: 'New Project',
    roles: ['landlord', 'realtor'],
    realtorTracks: ['rehab-specialist', 'property-manager'],
    rows: [],
  },
  {
    id: 'deal-analyzer',
    label: 'Deal Analyzer',
    icon: Calculator,
    description: 'Flip calculator and BRRR analyzer for investment decisions.',
    primaryLabel: 'Deal',
    secondaryLabel: 'Type',
    valueLabel: 'ROI',
    newActionLabel: 'New Analysis',
    roles: ['landlord', 'realtor'],
    realtorTracks: ['realtor'],
    rows: [],
  },
  {
    id: 'ui-overhaul',
    label: 'UI Overhaul Demo',
    icon: Sparkles,
    description: 'Integrated shadcn UI showcase with responsive component patterns.',
    primaryLabel: 'Component',
    secondaryLabel: 'State',
    valueLabel: 'Usage',
    newActionLabel: 'Preview Component',
    roles: ['tenant', 'realtor', 'landlord'],
    rows: [],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: Calculator,
    description: 'P&L, rent roll, expense tracking, and owner statements.',
    primaryLabel: 'Transaction',
    secondaryLabel: 'Category',
    valueLabel: 'Amount',
    newActionLabel: 'New Entry',
    roles: ['landlord', 'realtor'],
    realtorTracks: ['property-manager'],
    rows: [],
  },
  {
    id: 'leasing',
    label: 'Leasing',
    icon: Key,
    description: 'Vacancy pipeline, applications, showings, and lease renewals.',
    primaryLabel: 'Application',
    secondaryLabel: 'Unit',
    valueLabel: 'Rent',
    newActionLabel: 'New Application',
    roles: ['landlord', 'realtor'],
    realtorTracks: ['realtor', 'property-manager'],
    rows: [],
  },
  {
    id: 'work-order-board',
    label: 'Work Orders',
    icon: LayoutList,
    description: 'Visual kanban board for maintenance work orders.',
    primaryLabel: 'Work Order',
    secondaryLabel: 'Unit',
    valueLabel: 'Cost',
    newActionLabel: 'New Work Order',
    roles: ['landlord', 'realtor'],
    realtorTracks: ['rehab-specialist', 'property-manager'],
    rows: [],
  },
  {
    id: 'inbox',
    label: 'Inbox',
    icon: Mail,
    description: 'Unified messaging with tenants, vendors, and applicants.',
    primaryLabel: 'Message',
    secondaryLabel: 'Contact',
    valueLabel: 'Date',
    newActionLabel: 'Compose',
    roles: ['tenant', 'landlord', 'realtor'],
    rows: [],
  },
  {
    id: 'automation',
    label: 'Automation',
    icon: Zap,
    description: 'Rules engine for reminders, alerts, and workflow automation.',
    primaryLabel: 'Rule',
    secondaryLabel: 'Trigger',
    valueLabel: 'Runs',
    newActionLabel: 'New Rule',
    roles: ['landlord', 'realtor'],
    rows: [],
  },
  {
    id: 'lifecycle',
    label: 'Lifecycle',
    icon: GitBranch,
    description: 'Property lifecycle: Acquire → Renovate → Lease → Manage → Sell.',
    primaryLabel: 'Property',
    secondaryLabel: 'Stage',
    valueLabel: 'Value',
    newActionLabel: 'Add Property',
    roles: ['landlord', 'realtor'],
    realtorTracks: ['realtor', 'property-manager'],
    rows: [],
  },
]

const ROLE_DASHBOARD: Record<RoleType, RoleDashboardConfig> = {
  landlord: {
    heading: 'Landlord Command Center',
    subheading: 'Portfolio economics, occupancy, and risk monitoring.',
    kpis: [
      { label: 'Portfolio Value', value: '$2,820,000', delta: '+3.2%' },
      { label: 'Monthly Cash Flow', value: '$8,450', delta: '+6.1%' },
      { label: 'Rent Collected', value: '$12,600', delta: '+2.8%' },
      { label: 'Occupancy Rate', value: '93.6%', delta: '+1.4%' },
      { label: 'Pending Maintenance', value: '4', delta: '-8.3%' },
      { label: 'Cap Rate', value: '6.2%', delta: '+0.4%' },
      { label: 'ROI', value: '18.1%', delta: '+1.1%' },
      { label: 'Outstanding Rent', value: '$1,350', delta: '-11.0%' },
    ],
    trendData: [
      { month: 'Jan', income: 11.2, expenses: 4.4, cashflow: 6.8, occupancy: 89, value: 2.41, maintenance: 4.1, collected: 9.7, pipeline: 3.4, commission: 1.8 },
      { month: 'Feb', income: 11.4, expenses: 4.2, cashflow: 7.2, occupancy: 90, value: 2.47, maintenance: 3.8, collected: 10.0, pipeline: 3.8, commission: 1.9 },
      { month: 'Mar', income: 11.7, expenses: 4.5, cashflow: 7.2, occupancy: 92, value: 2.55, maintenance: 3.5, collected: 10.4, pipeline: 4.1, commission: 2.2 },
      { month: 'Apr', income: 11.9, expenses: 4.6, cashflow: 7.3, occupancy: 91, value: 2.63, maintenance: 3.6, collected: 10.8, pipeline: 4.7, commission: 2.4 },
      { month: 'May', income: 12.2, expenses: 4.7, cashflow: 7.5, occupancy: 93, value: 2.72, maintenance: 3.1, collected: 11.6, pipeline: 5.2, commission: 2.8 },
      { month: 'Jun', income: 12.6, expenses: 4.8, cashflow: 7.8, occupancy: 94, value: 2.82, maintenance: 2.9, collected: 12.1, pipeline: 5.6, commission: 3.1 },
    ],
    pieData: [
      { name: 'Collected', value: 12.6, color: '#0ea5a4' },
      { name: 'Outstanding', value: 1.35, color: '#f59e0b' },
      { name: 'Concessions', value: 0.45, color: '#64748b' },
    ],
    activity: [
      'Tenant at Riverstone Plaza paid rent via ACH',
      'Maintenance request resolved at Oakview Terrace',
      'Offer accepted for 18th Street Duplex',
      'Commission settled from seller-side closing',
    ],
    calendar: [
      { time: '09:00', title: 'Portfolio standup' },
      { time: '11:30', title: 'Inspection: 35 Harbor Way' },
      { time: '14:00', title: 'Buyer call: Midtown Townhomes' },
      { time: '16:00', title: 'Maintenance vendor review' },
    ],
  },
  realtor: {
    heading: 'Realtor Command Center',
    subheading: 'Pipeline velocity, showings, and commission performance.',
    kpis: [
      { label: 'Active Buyers', value: '47', delta: '+9.3%' },
      { label: 'Pending Closings', value: '11', delta: '+22.2%' },
      { label: 'Showings This Week', value: '34', delta: '+12.5%' },
      { label: 'Lead-to-Visit Rate', value: '41%', delta: '+3.1%' },
      { label: 'Commission Earned', value: '$92K', delta: '+4.7%' },
      { label: 'Deals Pipeline Value', value: '$9.4M', delta: '+7.8%' },
      { label: 'Avg Days to Close', value: '29', delta: '-6.2%' },
      { label: 'Open Tasks', value: '18', delta: '-10.0%' },
    ],
    trendData: [
      { month: 'Jan', income: 210, expenses: 122, cashflow: 88, occupancy: 70, value: 6.2, maintenance: 14, collected: 66, pipeline: 28, commission: 16 },
      { month: 'Feb', income: 224, expenses: 126, cashflow: 98, occupancy: 72, value: 6.5, maintenance: 13, collected: 69, pipeline: 30, commission: 18 },
      { month: 'Mar', income: 236, expenses: 131, cashflow: 105, occupancy: 73, value: 6.9, maintenance: 12, collected: 71, pipeline: 33, commission: 20 },
      { month: 'Apr', income: 248, expenses: 138, cashflow: 110, occupancy: 75, value: 7.2, maintenance: 12, collected: 74, pipeline: 37, commission: 22 },
      { month: 'May', income: 258, expenses: 141, cashflow: 117, occupancy: 77, value: 7.8, maintenance: 11, collected: 78, pipeline: 40, commission: 25 },
      { month: 'Jun', income: 270, expenses: 145, cashflow: 125, occupancy: 79, value: 8.3, maintenance: 10, collected: 82, pipeline: 44, commission: 27 },
    ],
    pieData: [
      { name: 'Qualified Leads', value: 44, color: '#0ea5a4' },
      { name: 'Warm Leads', value: 31, color: '#3b82f6' },
      { name: 'Cold Leads', value: 17, color: '#64748b' },
    ],
    activity: [
      'New buyer intake completed for Midtown Townhomes',
      'Tour confirmed at Harbor Heights unit 11D',
      'Offer package sent for Brookside Duplex',
      'Commission settlement posted for Lakepoint close',
    ],
    calendar: [
      { time: '08:30', title: 'Buyer strategy sync' },
      { time: '10:15', title: 'Showing: Harbor Heights 11D' },
      { time: '13:00', title: 'Listing consult: Crestview' },
      { time: '15:30', title: 'Offer review call' },
    ],
  },
  tenant: {
    heading: 'Tenant Command Center',
    subheading: 'Lease, payments, maintenance, and move-in planning.',
    kpis: [
      { label: 'Current Rent', value: '$2,850', delta: 'Due in 9 days' },
      { label: 'Lease Days Remaining', value: '214', delta: '+1 day' },
      { label: 'Open Maintenance', value: '2', delta: '-1 this week' },
      { label: 'Upcoming Appointments', value: '3', delta: '+1 today' },
      { label: 'Documents Pending', value: '1', delta: 'Needs signature' },
      { label: 'Messages Unread', value: '4', delta: '+2 today' },
      { label: 'Payment Health Score', value: '96', delta: '+2' },
      { label: 'Utility Budget', value: '$310', delta: '-4.5%' },
    ],
    trendData: [
      { month: 'Jan', income: 32, expenses: 28, cashflow: 4, occupancy: 96, value: 2.2, maintenance: 7, collected: 92, pipeline: 5, commission: 0 },
      { month: 'Feb', income: 34, expenses: 29, cashflow: 5, occupancy: 96, value: 2.2, maintenance: 6, collected: 93, pipeline: 5, commission: 0 },
      { month: 'Mar', income: 35, expenses: 30, cashflow: 5, occupancy: 97, value: 2.3, maintenance: 6, collected: 94, pipeline: 6, commission: 0 },
      { month: 'Apr', income: 36, expenses: 30, cashflow: 6, occupancy: 97, value: 2.3, maintenance: 5, collected: 95, pipeline: 6, commission: 0 },
      { month: 'May', income: 37, expenses: 31, cashflow: 6, occupancy: 98, value: 2.4, maintenance: 4, collected: 96, pipeline: 7, commission: 0 },
      { month: 'Jun', income: 39, expenses: 32, cashflow: 7, occupancy: 98, value: 2.4, maintenance: 4, collected: 97, pipeline: 7, commission: 0 },
    ],
    pieData: [
      { name: 'Rent Paid', value: 97, color: '#0ea5a4' },
      { name: 'Utilities', value: 21, color: '#3b82f6' },
      { name: 'Fees', value: 4, color: '#f59e0b' },
    ],
    activity: [
      'Maintenance ticket updated: Kitchen faucet replacement',
      'Rent reminder posted for upcoming due date',
      'Lease document ready for e-signature',
      'Amenity reservation confirmed for community room',
    ],
    calendar: [
      { time: '09:30', title: 'Maintenance visit: Kitchen faucet' },
      { time: '12:00', title: 'Lease check-in with manager' },
      { time: '17:00', title: 'Utility billing review' },
      { time: '18:30', title: 'Move-in planning checklist' },
    ],
  },
}

function readStoredUser(): AuthUser | null {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AuthUser>
    if (!parsed?.id || !parsed?.email) {
      return null
    }

    const tokenFromUser = typeof parsed.sessionToken === 'string' ? parsed.sessionToken.trim() : ''
    const tokenFromStorage = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY)?.trim() || ''
    const sessionToken = tokenFromUser || tokenFromStorage

    // Legacy sessions without a token must re-authenticate to avoid stale local-only workspace state.
    if (!sessionToken) {
      return null
    }

    return {
      id: parsed.id,
      name: parsed.name || 'User',
      email: parsed.email,
      username: parsed.username || parsed.email.split('@')[0] || 'user',
      role: (parsed.role || 'realtor') as RoleType,
      tutorialCompleted: Boolean(parsed.tutorialCompleted),
      isDemo: Boolean(parsed.isDemo),
      sessionToken,
    }
  } catch {
    return null
  }
}

function statusVariant(status: RowStatus): 'success' | 'warning' | 'danger' | 'info' {
  if (status === 'Active' || status === 'Closed') {
    return 'success'
  }
  if (status === 'Pending') {
    return 'warning'
  }
  return 'danger'
}

function exportToCsv(rows: DataRow[]) {
  const header = ['Primary', 'Secondary', 'Status', 'Value', 'Updated']
  const body = rows.map((row) => [row.primary, row.secondary, row.status, row.value.toString(), row.updatedAt])
  const csvContent = [header, ...body].map((line) => line.join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'module-export.csv'
  link.click()
}

export function DashboardModule({ role, realtorTrack, onOpenDetail }: { role: RoleType; realtorTrack: RealtorTrack | 'all'; onOpenDetail: (title: string) => void }) {
  const model = ROLE_DASHBOARD[role]
  const latestTrend = model.trendData[model.trendData.length - 1]
  const profitLossValue = Math.round((latestTrend.income - latestTrend.expenses) * 1000)
  const profitLossDelta = ((latestTrend.income - latestTrend.expenses) / Math.max(latestTrend.expenses, 1)) * 100
  const isProfit = profitLossValue >= 0

  const profitLossCard: KpiItem = {
    label: 'Profit/Loss',
    value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.abs(profitLossValue)),
    delta: `${isProfit ? 'Profit' : 'Loss'} • ${profitLossDelta >= 0 ? '+' : ''}${profitLossDelta.toFixed(1)}%`,
    tone: isProfit ? 'profit' : 'loss',
  }

  const summaryCards = role === 'tenant' ? model.kpis : [profitLossCard, ...model.kpis]
  const pieTotal = model.pieData.reduce((total, slice) => total + slice.value, 0)
  const outstandingSlice = model.pieData.find((slice) => slice.name.toLowerCase().includes('outstanding'))
  const concessionsSlice = model.pieData.find((slice) => slice.name.toLowerCase().includes('concessions'))
  const outstandingValue = (outstandingSlice?.value || 0) * 1000
  const concessionsValue = (concessionsSlice?.value || 0) * 1000
  const netAvailableValue = Math.max(0, Math.round(latestTrend.collected * 1000 - outstandingValue))

  const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)

  const formatPieValue = (value: number) => {
    if (role === 'landlord') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value * 1000)
    }
    if (role === 'realtor') {
      return `${Math.round(value)} leads`
    }
    return `${Math.round(value)}%`
  }

  const formattedPieTotal = formatPieValue(pieTotal)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{model.heading}</CardTitle>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{model.subheading}</p>
            {role === 'realtor' && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 font-medium text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-300">
                  Persona Hub
                </span>
                <span className="text-slate-600 dark:text-slate-300">Unified workspace: Realtor + Rehab Specialist + Property Manager</span>
                <span className="rounded-full border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Focus: {REALTOR_TRACK_LABELS[realtorTrack]}
                </span>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {role === 'landlord' && (
        <div className="space-y-3 rounded-3xl bg-gradient-to-b from-blue-50 to-sky-100 p-3 text-slate-900 shadow-md md:hidden">
          <div className="rounded-2xl border border-blue-200 bg-white p-3 text-slate-900 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-blue-700">Account Overview</p>
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">Landlord</span>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Net Available</p>
            <p className="text-3xl font-bold leading-none text-slate-900">{formatCurrency(netAvailableValue)}</p>
            <p className="mt-1 text-[11px] text-slate-500">After {formatCurrency(outstandingValue)} outstanding dues</p>

            <div className="mt-3 space-y-2 rounded-xl border border-blue-100 bg-blue-50 p-2.5 text-xs">
              <div className="grid grid-cols-[92px_1fr] items-center gap-2">
                <div className="relative h-[92px] w-[92px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={model.pieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={28}
                        outerRadius={41}
                        paddingAngle={1}
                        cornerRadius={6}
                        startAngle={90}
                        endAngle={-270}
                      >
                        {model.pieData.map((entry) => (
                          <Cell key={`mini-${entry.name}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Rent Collected</span>
                    <span className="font-semibold text-slate-900">{formatCurrency(latestTrend.collected * 1000)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Outstanding Rent</span>
                    <span className="font-semibold text-rose-600">-{formatCurrency(outstandingValue)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Concessions</span>
                    <span className="font-semibold text-amber-600">-{formatCurrency(concessionsValue)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {summaryCards.map((item, index) => (
              <motion.div key={`mobile-${item.label}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.015 }}>
                <Card className={`p-2.5 shadow-sm ${item.tone === 'profit' ? 'border-emerald-300 bg-emerald-50' : item.tone === 'loss' ? 'border-rose-300 bg-rose-50' : 'border-sky-200 bg-white'}`}>
                  <p className="text-[11px] leading-tight text-slate-500">{item.label}</p>
                  <p className="mt-1 text-base font-semibold leading-tight text-slate-900">{item.value}</p>
                  <p className={`text-[11px] leading-tight ${item.tone === 'loss' ? 'text-rose-600' : 'text-emerald-600'}`}>{item.delta}</p>
                </Card>
              </motion.div>
            ))}
          </div>

        </div>
      )}

      <div className={cn('grid grid-cols-2 gap-2 xl:grid-cols-5', role === 'landlord' && 'hidden md:grid')}>
        {summaryCards.map((item, index) => (
          <motion.div key={item.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.02 }}>
            <Card className={`p-2.5 shadow-sm xl:p-3 ${item.tone === 'profit' ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/20' : item.tone === 'loss' ? 'border-rose-200 bg-rose-50/60 dark:border-rose-900/60 dark:bg-rose-950/20' : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'}`}>
              <p className="text-[11px] leading-tight text-slate-500 dark:text-slate-400 xl:text-xs">{item.label}</p>
              <p className="mt-1 text-base font-semibold leading-tight text-slate-900 dark:text-slate-50 xl:text-xl">{item.value}</p>
              <p className={`text-[11px] leading-tight xl:text-xs ${item.tone === 'loss' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{item.delta}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      <Card className={cn('border-emerald-100 bg-gradient-to-b from-white to-emerald-50/40 dark:border-slate-700 dark:from-slate-900 dark:to-slate-800', role === 'landlord' && 'hidden md:block')}>
        <CardHeader>
          <CardTitle>Rent Distribution</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_190px]">
          <div className="relative">
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie
                  data={model.pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={62}
                  outerRadius={88}
                  paddingAngle={2}
                >
                  {model.pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatPieValue(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="rounded-full border border-emerald-200 bg-white/95 px-3 py-2 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900/95">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Total</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formattedPieTotal}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 text-xs">
            {model.pieData.map((slice) => {
              const pct = Math.round((slice.value / Math.max(pieTotal, 1)) * 100)
              return (
                <div key={slice.name} className="rounded-xl border border-slate-200 bg-white/90 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/70">
                  <p className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: slice.color }} />
                    {slice.name}
                  </p>
                  <p className="mt-1 font-semibold text-slate-900 dark:text-slate-100">{formatPieValue(slice.value)}</p>
                  <p className="text-slate-500 dark:text-slate-400">{pct}% of total</p>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <div className={cn('grid grid-cols-1 gap-4 xl:grid-cols-3', role === 'landlord' && 'hidden md:grid')}>
        <ChartCard title="Cash Flow Over Time">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={model.trendData}>
              <defs>
                <linearGradient id="cashFlow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5a4" stopOpacity={0.32} />
                  <stop offset="95%" stopColor="#0ea5a4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#dbeafe" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="cashflow" stroke="#0ea5a4" fill="url(#cashFlow)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Income vs Expenses">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="income" fill="#0ea5a4" />
              <Bar dataKey="expenses" fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Occupancy Trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="occupancy" stroke="#2563eb" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Property Value Growth">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Maintenance Cost Trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="maintenance" stroke="#f59e0b" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Rent Collection Trend">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="collected" stroke="#0ea5a4" fill="#99f6e4" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lead Pipeline">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="pipeline" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Commission Revenue">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={model.trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="commission" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className={cn('grid grid-cols-1 gap-4 xl:grid-cols-3', role === 'landlord' && 'hidden md:grid')}>
        <Card>
          <CardHeader>
            <CardTitle>Activity Feed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {model.activity.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => onOpenDetail(item)}
                className="flex w-full items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-3 text-left text-sm text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <span>{item}</span>
                <ChevronRight className="h-4 w-4 shrink-0" />
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Calendar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {model.calendar.map((item) => (
              <div key={item.time} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
                <p className="text-xs text-slate-500 dark:text-slate-400">{item.time}</p>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.title}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="border-emerald-100 bg-gradient-to-b from-white to-emerald-50/40 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:to-slate-800">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function ModuleWorkspace({
  module,
  query,
  onOpenDetail,
}: {
  module: ModuleConfig
  query: string
  onOpenDetail: (title: string) => void
}) {
  const [rows, setRows] = useState<DataRow[]>(module.rows)
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({})

  const addModuleRow = () => {
    const now = Date.now()
    const serial = rows.length + 1
    const defaultSecondary = module.rows[0]?.secondary || 'General'
    const defaultValue = module.rows[0]?.value || 0

    const newRow: DataRow = {
      id: `${module.id}-new-${now}`,
      primary: `New ${module.primaryLabel} ${serial}`,
      secondary: defaultSecondary,
      status: 'Pending',
      value: defaultValue,
      updatedAt: 'Just now',
    }

    setRows((prev) => [newRow, ...prev])
  }

  useEffect(() => {
    setRows(module.rows)
    setSelectedRows({})
  }, [module])

  const filterForm = useForm<{ status: string; secondary: string }>({
    defaultValues: { status: 'All', secondary: 'All' },
  })

  const statusFilter = filterForm.watch('status')
  const secondaryFilter = filterForm.watch('secondary')

  const secondaryOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.secondary))),
    [rows],
  )

  const filteredRows = useMemo(() => rows.filter((row) => {
    const q = query.trim().toLowerCase()
    const matchesSearch = q.length === 0 || `${row.primary} ${row.secondary}`.toLowerCase().includes(q)
    const matchesStatus = statusFilter === 'All' || row.status === statusFilter
    const matchesSecondary = secondaryFilter === 'All' || row.secondary === secondaryFilter
    return matchesSearch && matchesStatus && matchesSecondary
  }), [query, rows, secondaryFilter, statusFilter])

  const columns = useMemo<ColumnDef<DataRow>[]>(() => [
    {
      id: 'select',
      header: () => (
        <input
          aria-label="Select all"
          type="checkbox"
          checked={Object.keys(selectedRows).length > 0 && filteredRows.every((row) => selectedRows[row.id])}
          onChange={(event) => {
            const checked = event.target.checked
            setSelectedRows(checked ? Object.fromEntries(filteredRows.map((row) => [row.id, true])) : {})
          }}
        />
      ),
      cell: ({ row }) => (
        <input
          aria-label={`Select ${row.original.primary}`}
          type="checkbox"
          checked={Boolean(selectedRows[row.original.id])}
          onChange={(event) => {
            setSelectedRows((prev) => {
              if (event.target.checked) {
                return { ...prev, [row.original.id]: true }
              }
              const next = { ...prev }
              delete next[row.original.id]
              return next
            })
          }}
        />
      ),
    },
    {
      accessorKey: 'primary',
      header: module.primaryLabel,
      cell: ({ row }) => (
        <button type="button" className="text-left font-medium" onClick={() => onOpenDetail(row.original.primary)}>
          {row.original.primary}
        </button>
      ),
    },
    { accessorKey: 'secondary', header: module.secondaryLabel },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <select
          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
          value={row.original.status}
          onChange={(event) => {
            const next = event.target.value as RowStatus
            setRows((prev) => prev.map((item) => (item.id === row.original.id ? { ...item, status: next, updatedAt: 'Just now' } : item)))
          }}
        >
          <option>Active</option>
          <option>Pending</option>
          <option>At Risk</option>
          <option>Closed</option>
        </select>
      ),
    },
    {
      accessorKey: 'value',
      header: module.valueLabel,
      cell: ({ row }) => (
        <input
          className="w-24 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right text-xs dark:border-slate-700 dark:bg-slate-900"
          value={row.original.value}
          onChange={(event) => {
            const next = Number(event.target.value || 0)
            setRows((prev) => prev.map((item) => (item.id === row.original.id ? { ...item, value: next, updatedAt: 'Just now' } : item)))
          }}
        />
      ),
    },
    { accessorKey: 'updatedAt', header: 'Updated' },
  ], [filteredRows, module.primaryLabel, module.secondaryLabel, module.valueLabel, onOpenDetail, selectedRows])

  const table = useReactTable({
    data: filteredRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const selectedCount = Object.keys(selectedRows).length

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>{module.label}</CardTitle>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{module.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => exportToCsv(filteredRows)}>
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            <Button variant="secondary" size="sm" onClick={() => window.print()}>
              <Download className="mr-1 h-4 w-4" /> Export PDF
            </Button>
            <Button size="sm" onClick={addModuleRow}>
              <Plus className="mr-1 h-4 w-4" /> {module.newActionLabel}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form className="grid grid-cols-1 gap-2 md:grid-cols-3" onSubmit={(e) => e.preventDefault()}>
            <label className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
              Status
              <select className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 dark:border-slate-700 dark:bg-slate-900" {...filterForm.register('status')}>
                <option>All</option>
                <option>Active</option>
                <option>Pending</option>
                <option>At Risk</option>
                <option>Closed</option>
              </select>
            </label>
            <label className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
              {module.secondaryLabel}
              <select className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 dark:border-slate-700 dark:bg-slate-900" {...filterForm.register('secondary')}>
                <option>All</option>
                {secondaryOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
              Bulk actions
              <div className="flex h-10 items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs dark:border-slate-700 dark:bg-slate-800">
                <span>{selectedCount > 0 ? `${selectedCount} selected` : 'Select rows for bulk actions'}</span>
                <Button
                  type="button"
                  aria-label="Remove selected"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={selectedCount === 0}
                  onClick={() => {
                    setRows((prev) => prev.filter((row) => !selectedRows[row.id]))
                    setSelectedRows({})
                  }}
                >
                  Remove selected
                </Button>
              </div>
            </label>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 text-slate-700 dark:text-slate-200">
                      {cell.column.id === 'status' ? (
                        <div className="flex items-center gap-2">
                          <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}

export default function App() {
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => readStoredUser())
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login')
  const [authUsername, setAuthUsername] = useState('')
  const [authPassword, setAuthPassword] = useState('')
  const [signupName, setSignupName] = useState('')
  const [signupEmail, setSignupEmail] = useState('')
  const [signupUsername, setSignupUsername] = useState('')
  const [signupPassword, setSignupPassword] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [authBusy, setAuthBusy] = useState(false)
  const [tutorialOpen, setTutorialOpen] = useState(false)

  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') {
      return 'light'
    }
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'dark' ? 'dark' : 'light'
  })
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [activeModule, setActiveModule] = useState<ModuleId>('dashboard')
  const [workspace, setWorkspace] = useState<OperatorWorkspace | null>(null)
  const [globalQuery, setGlobalQuery] = useState('')
  const [commandQuery, setCommandQuery] = useState('')
  const [commandOpen, setCommandOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailTitle, setDetailTitle] = useState('Select any row or event to inspect details.')
  const [workspaceSyncState, setWorkspaceSyncState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [workspaceLastSyncedAt, setWorkspaceLastSyncedAt] = useState<string | null>(null)
  const [propertiesAddRequestId, setPropertiesAddRequestId] = useState(0)
  const [realtorTrack, setRealtorTrack] = useState<RealtorTrack | 'all'>(() => {
    if (typeof window === 'undefined') {
      return 'all'
    }
    const stored = window.localStorage.getItem(REALTOR_TRACK_STORAGE_KEY)
    if (stored === 'realtor' || stored === 'rehab-specialist' || stored === 'property-manager' || stored === 'all') {
      return stored
    }
    return 'all'
  })

  const role: RoleType = authUser?.role ?? 'landlord'

  const visibleModules = useMemo(() => {
    const byRole = MODULES.filter((module) => module.roles.includes(role))
    const byTrack = role !== 'realtor' || realtorTrack === 'all'
      ? byRole
      : byRole.filter((module) => !module.realtorTracks || module.realtorTracks.includes(realtorTrack))

    if (!MIXED_OPERATOR_MODE) {
      return byTrack
    }

    const byId = new Map(byTrack.map((module) => [module.id, module]))
    return UNIFIED_MODULE_ORDER
      .map((id) => byId.get(id))
      .filter((module): module is ModuleConfig => Boolean(module))
  }, [role, realtorTrack])

  const activeModuleConfig = useMemo(
    () => visibleModules.find((module) => module.id === activeModule) || visibleModules[0],
    [activeModule, visibleModules],
  )

  const mobilePrimaryModules = useMemo(() => {
    const byId = new Map(visibleModules.map((module) => [module.id, module]))
    return MOBILE_PRIMARY_MODULE_ORDER
      .map((id) => byId.get(id))
      .filter((module): module is ModuleConfig => Boolean(module))
      .slice(0, 5)
  }, [visibleModules])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setCommandOpen((prev) => !prev)
      }
      if (event.key === 'Escape') {
        setCommandOpen(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (!activeModuleConfig) {
      return
    }
    if (activeModule !== activeModuleConfig.id) {
      setActiveModule(activeModuleConfig.id)
    }
  }, [activeModule, activeModuleConfig])

  const commandItems = useMemo(() => {
    const q = commandQuery.trim().toLowerCase()
    return visibleModules.filter((item) => item.label.toLowerCase().includes(q))
  }, [commandQuery, visibleModules])

  const openDetail = (title: string) => {
    setDetailTitle(title)
    setDetailOpen(true)
  }

  const openPropertiesAddForm = () => {
    setActiveModule('properties')
    setPropertiesAddRequestId((prev) => prev + 1)
  }

  useEffect(() => {
    if (!authUser) {
      setWorkspace(null)
      return
    }

    let cancelled = false
    void readWorkspaceForUser({
      id: authUser.id,
      isDemo: authUser.isDemo,
      name: authUser.name,
      sessionToken: authUser.sessionToken,
    }).then((nextWorkspace) => {
      if (!cancelled) {
        setWorkspace(nextWorkspace)
      }
    })

    return () => {
      cancelled = true
    }
  }, [authUser])

  useEffect(() => {
    if (!authUser || !workspace) {
      return
    }

    let cancelled = false
    setWorkspaceSyncState('saving')

    void (async () => {
      try {
        await writeWorkspaceForUser({
          id: authUser.id,
          isDemo: authUser.isDemo,
          name: authUser.name,
          sessionToken: authUser.sessionToken,
        }, workspace)
        if (!cancelled) {
          setWorkspaceSyncState('saved')
          setWorkspaceLastSyncedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
        }
      } catch {
        if (!cancelled) {
          setWorkspaceSyncState('error')
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [authUser, workspace])

  const switchRole = (nextRole: RoleType) => {
    if (MIXED_OPERATOR_MODE) {
      return
    }
    if (!authUser) {
      return
    }
    const nextUser = { ...authUser, role: nextRole }
    setAuthUser(nextUser)
    window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(nextUser))
    setActiveModule('dashboard')
    setCommandOpen(false)
    setCommandQuery('')
  }

  const switchRealtorTrack = (nextTrack: RealtorTrack | 'all') => {
    setRealtorTrack(nextTrack)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(REALTOR_TRACK_STORAGE_KEY, nextTrack)
    }
    setActiveModule('dashboard')
  }

  const toAuthUser = (profile: {
    id: string
    full_name: string
    email: string
    username: string
    tutorial_completed: boolean
    is_demo: boolean
    session_token?: string
  }): AuthUser => ({
    id: profile.id,
    name: profile.full_name,
    email: profile.email,
    username: profile.username,
    role: 'realtor',
    tutorialCompleted: profile.tutorial_completed,
    isDemo: profile.is_demo,
    sessionToken: profile.session_token,
  })

  const persistAuthUser = (user: AuthUser) => {
    setAuthUser(user)
    window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user))
    if (user.sessionToken) {
      window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, user.sessionToken)
    }
  }

  const handleLogin = async () => {
    setAuthBusy(true)
    setAuthError(null)
    try {
      const profile = await loginAuthProfile({
        username: authUsername.trim().toLowerCase(),
        password: authPassword,
      })
      const user = toAuthUser(profile)
      persistAuthUser(user)
      setTutorialOpen(!user.tutorialCompleted)
      setActiveModule('dashboard')
      setAuthPassword('')
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Unable to sign in.')
    } finally {
      setAuthBusy(false)
    }
  }

  const handleSignup = async () => {
    setAuthBusy(true)
    setAuthError(null)
    try {
      const profile = await signupAuthProfile({
        full_name: signupName.trim(),
        email: signupEmail.trim().toLowerCase(),
        username: signupUsername.trim().toLowerCase(),
        password: signupPassword,
      })
      const user = toAuthUser(profile)
      persistAuthUser(user)
      setTutorialOpen(true)
      setActiveModule('dashboard')
      setSignupPassword('')
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Unable to create account.')
    } finally {
      setAuthBusy(false)
    }
  }

  const handleCompleteTutorial = async () => {
    if (!authUser) {
      return
    }
    try {
      const profile = await completeTutorial(authUser.id)
      const updated = {
        ...toAuthUser(profile),
        sessionToken: profile.session_token || authUser.sessionToken,
      }
      persistAuthUser(updated)
      setTutorialOpen(false)
    } catch {
      // Keep UI usable even if API call fails transiently.
      const fallback = { ...authUser, tutorialCompleted: true }
      persistAuthUser(fallback)
      setTutorialOpen(false)
    }
  }

  const handleSignOut = () => {
    setAuthUser(null)
    setWorkspace(null)
    setTutorialOpen(false)
    window.localStorage.removeItem(AUTH_USER_STORAGE_KEY)
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  }

  useEffect(() => {
    if (!MIXED_OPERATOR_MODE || !authUser) {
      return
    }
    if (authUser.role !== 'realtor') {
      const normalizedUser = { ...authUser, role: 'realtor' as RoleType }
      setAuthUser(normalizedUser)
      window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(normalizedUser))
    }
    if (realtorTrack !== 'all') {
      setRealtorTrack('all')
      window.localStorage.setItem(REALTOR_TRACK_STORAGE_KEY, 'all')
    }
  }, [authUser, realtorTrack])

  useEffect(() => {
    if (!authUser) {
      return
    }
    if (!authUser.tutorialCompleted) {
      setTutorialOpen(true)
    }
  }, [authUser])

  if (!authUser) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 p-4 dark:bg-slate-950">
        <Card className="w-full max-w-md p-6">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">RealAssist Sign In</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Use demo account for quick access or create your own account.</p>

          <div className="mt-4 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
            <button
              type="button"
              className={cn('rounded-lg px-3 py-2 text-sm font-medium transition', authMode === 'login' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400')}
              onClick={() => {
                setAuthMode('login')
                setAuthError(null)
              }}
            >
              Login
            </button>
            <button
              type="button"
              className={cn('rounded-lg px-3 py-2 text-sm font-medium transition', authMode === 'signup' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400')}
              onClick={() => {
                setAuthMode('signup')
                setAuthError(null)
              }}
            >
              Sign Up
            </button>
          </div>

          {authMode === 'login' ? (
            <form
              className="mt-4 space-y-3"
              onSubmit={(event) => {
                event.preventDefault()
                void handleLogin()
              }}
            >
              <Input aria-label="Username" placeholder="demo" value={authUsername} onChange={(e) => setAuthUsername(e.target.value)} />
              <Input aria-label="Password" type="password" placeholder="demo123" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} />
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/25 dark:text-emerald-300">
                Demo credentials: username `demo` and password `demo123`
              </div>
              {authError && <p className="text-sm text-rose-600">{authError}</p>}
              <Button type="submit" className="w-full" disabled={authBusy}>{authBusy ? 'Signing in...' : 'Continue'}</Button>
            </form>
          ) : (
            <form
              className="mt-4 space-y-3"
              onSubmit={(event) => {
                event.preventDefault()
                void handleSignup()
              }}
            >
              <Input aria-label="Full Name" placeholder="Alex Rivera" value={signupName} onChange={(e) => setSignupName(e.target.value)} />
              <Input aria-label="Email" placeholder="alex@company.com" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} />
              <Input aria-label="Username" placeholder="alex.rivera" value={signupUsername} onChange={(e) => setSignupUsername(e.target.value)} />
              <Input aria-label="Password" type="password" placeholder="Create a password" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} />
              {authError && <p className="text-sm text-rose-600">{authError}</p>}
              <Button type="submit" className="w-full" disabled={authBusy}>{authBusy ? 'Creating account...' : 'Create Account'}</Button>
            </form>
          )}

          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">First-time accounts receive a short onboarding tutorial and a persistent personal workspace on this device.</p>
        </Card>
      </div>
    )
  }

  if (!workspace) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-100 p-4 dark:bg-slate-950">
        <Card className="w-full max-w-sm p-6">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Loading your workspace...</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-[auto_1fr]">
        <aside className={cn('hidden border-r border-slate-200 bg-white/95 p-2 transition-all dark:border-slate-800 dark:bg-slate-900/95 md:block', sidebarCollapsed ? 'w-[76px]' : 'w-[252px]')}>
          <div className="flex items-center justify-between px-2 py-2">
            {!sidebarCollapsed && <p className="text-sm font-semibold">RealAssist</p>}
            <Button variant="ghost" size="icon" aria-label="Toggle sidebar" onClick={() => setSidebarCollapsed((prev) => !prev)}>
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
          </div>

          {role === 'realtor' && !sidebarCollapsed && !MIXED_OPERATOR_MODE && (
            <div className="mb-2 mt-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-2 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Persona Focus</p>
              <select
                aria-label="Switch role"
                className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium shadow-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
                value={realtorTrack}
                onChange={(e) => switchRealtorTrack(e.target.value as RealtorTrack | 'all')}
              >
                {(['all', 'realtor', 'rehab-specialist', 'property-manager'] as const).map(t => (
                  <option key={t} value={t}>{REALTOR_TRACK_LABELS[t]}</option>
                ))}
              </select>
            </div>
          )}

          <nav className="mt-0 space-y-1" data-testid="nav-menu">
            {visibleModules.map((module) => {
              const Icon = module.icon
              return (
                <button
                  key={module.id}
                  type="button"
                  onClick={() => setActiveModule(module.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition',
                    activeModule === module.id
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {!sidebarCollapsed && <span>{module.label}</span>}
                </button>
              )
            })}
          </nav>

          <div className="mt-4 space-y-2 border-t border-slate-200 px-2 pt-3 dark:border-slate-800">
            {!sidebarCollapsed && !MIXED_OPERATOR_MODE && (
              <>
                <label className="sr-only" htmlFor="switch-role-sidebar">Switch role</label>
                <select
                  id="switch-role-sidebar"
                  aria-label="Switch role"
                  className="h-9 w-full rounded-xl border border-slate-200 bg-white px-3 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900"
                  value={role}
                  onChange={(event) => switchRole(event.target.value as RoleType)}
                >
                  <option value="tenant">Tenant View</option>
                  <option value="realtor">Realtor View</option>
                  <option value="landlord">Landlord View</option>
                </select>
              </>
            )}
            <div className={cn('flex gap-2', sidebarCollapsed ? 'flex-col' : 'flex-row')}>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Notifications"
                onClick={() => setActiveModule('notifications')}
                className={cn('h-9', sidebarCollapsed ? 'w-full' : 'flex-1')}
              >
                <Bell className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Toggle dark mode"
                onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
                className={cn('h-9', sidebarCollapsed ? 'w-full' : 'flex-1')}
              >
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
            </div>
            <Button variant="secondary" onClick={handleSignOut} className={cn('h-9', sidebarCollapsed ? 'w-full px-0' : 'w-full')}>
              <LogOut className={cn('h-4 w-4', sidebarCollapsed ? '' : 'mr-1')} /> {!sidebarCollapsed && 'Sign Out'}
            </Button>
          </div>
        </aside>

        <div className="relative min-w-0">
          <header className="sticky top-0 z-20 border-b border-slate-200 bg-gradient-to-b from-emerald-50/80 to-slate-100/90 backdrop-blur dark:border-slate-800 dark:from-slate-900 dark:to-slate-950/90">
            <div className="grid grid-cols-3 gap-2 p-2 md:flex md:flex-wrap md:items-center md:gap-2 md:p-3">
              <div className="relative col-span-2 w-full flex-1 md:min-w-[220px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  aria-label="Global Search"
                  className="h-10 rounded-xl border-slate-200 bg-white/95 pl-9 shadow-sm dark:border-slate-700 dark:bg-slate-900"
                  placeholder="Search properties, deals, contacts..."
                  value={globalQuery}
                  onChange={(event) => setGlobalQuery(event.target.value)}
                />
              </div>
              <Button className="hidden md:inline-flex" variant="secondary" onClick={() => setCommandOpen(true)}>
                <Command className="mr-1 h-4 w-4" /> Cmd/Ctrl + K
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="hidden h-9 w-9 md:inline-flex"
                aria-label="Notifications"
                onClick={() => setActiveModule('notifications')}
              >
                <Bell className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="hidden h-9 w-9 md:inline-flex"
                aria-label="Toggle dark mode"
                onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
              >
                {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Button>
              <Button className="hidden md:inline-flex" variant="secondary" onClick={openPropertiesAddForm}>
                <Plus className="mr-1 h-4 w-4" /> Add Property
              </Button>
              {authUser && (
                <span
                  data-testid="workspace-sync-status"
                  className={cn(
                    'hidden items-center rounded-full border px-2.5 py-1 text-[11px] font-medium md:inline-flex',
                    workspaceSyncState === 'saving' && 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300',
                    workspaceSyncState === 'saved' && 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-300',
                    workspaceSyncState === 'error' && 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-300',
                    workspaceSyncState === 'idle' && 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
                  )}
                >
                  {workspaceSyncState === 'saving' && 'Saving workspace...'}
                  {workspaceSyncState === 'saved' && `Saved${workspaceLastSyncedAt ? ` ${workspaceLastSyncedAt}` : ''}`}
                  {workspaceSyncState === 'error' && 'Save failed'}
                  {workspaceSyncState === 'idle' && 'Not synced yet'}
                </span>
              )}
              <label className="sr-only" htmlFor="switch-role">Switch role</label>
              <div className="col-span-1 flex items-center gap-2 md:hidden">
                {!MIXED_OPERATOR_MODE && (
                  <select
                    id="switch-role"
                    aria-label="Switch role"
                    className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900"
                    value={role}
                    onChange={(event) => switchRole(event.target.value as RoleType)}
                  >
                    <option value="tenant">Tenant</option>
                    <option value="realtor">Realtor</option>
                    <option value="landlord">Landlord</option>
                  </select>
                )}
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Notifications"
                  onClick={() => setActiveModule('notifications')}
                >
                  <Bell className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Toggle dark mode"
                  onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
                >
                  {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </Button>
                <Button variant="secondary" size="icon" className="h-9 w-9 shrink-0" aria-label="Add Property" onClick={openPropertiesAddForm}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="border-t border-slate-200 px-2 pb-2 pt-2 dark:border-slate-800 md:hidden" data-testid="nav-menu-mobile">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {visibleModules.map((module) => {
                  const Icon = module.icon
                  return (
                    <button
                      key={module.id}
                      type="button"
                      onClick={() => setActiveModule(module.id)}
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition',
                        activeModule === module.id
                          ? 'border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900'
                          : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{module.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </header>

          <main className="space-y-4 p-2 pb-32 md:p-3 md:pb-3" data-testid="main-content">
            {activeModuleConfig?.id === 'dashboard' ? (
              <OperatorDashboardModule
                userName={authUser.name}
                deals={workspace.deals}
                properties={workspace.properties}
                projects={workspace.projects}
                onOpenModule={setActiveModule}
              />
            ) : activeModuleConfig?.id === 'investor-os' ? (
              <InvestorOSModule onOpenModule={setActiveModule} />
            ) : activeModuleConfig?.id === 'properties' ? (
              <PropertiesPipelineModule
                properties={workspace.properties}
                addFormRequestId={propertiesAddRequestId}
                onPropertiesChange={(properties) => setWorkspace((prev) => (prev ? { ...prev, properties } : prev))}
              />
            ) : activeModuleConfig?.id === 'accounting' ? (
              <AccountingModule />
            ) : activeModuleConfig?.id === 'leasing' ? (
              <LeasingModule />
            ) : activeModuleConfig?.id === 'work-order-board' ? (
              <KanbanModule />
            ) : activeModuleConfig?.id === 'inbox' ? (
              <InboxModule />
            ) : activeModuleConfig?.id === 'automation' ? (
              <AutomationModule />
            ) : activeModuleConfig?.id === 'lifecycle' ? (
              <LifecycleModule />
            ) : activeModuleConfig?.id === 'construction' ? (
              <ConstructionModule
                projects={workspace.projects}
                onProjectsChange={(projects) => setWorkspace((prev) => (prev ? { ...prev, projects } : prev))}
              />
            ) : activeModuleConfig?.id === 'deals-pipeline' ? (
              <DealPipelineModule
                deals={workspace.deals}
                onDealsChange={(deals) => setWorkspace((prev) => (prev ? { ...prev, deals } : prev))}
              />
            ) : activeModuleConfig?.id === 'deal-analyzer' ? (
              <DealAnalyzer />
            ) : activeModuleConfig?.id === 'ui-overhaul' ? (
              <DemoOne />
            ) : (
              activeModuleConfig && <ModuleWorkspace module={activeModuleConfig} query={globalQuery} onOpenDetail={openDetail} />
            )}
          </main>

          <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-2 py-2 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 md:hidden" data-testid="mobile-bottom-nav">
            <div className="grid grid-cols-5 gap-1">
              {mobilePrimaryModules.map((module) => {
                const Icon = module.icon
                const isActive = activeModule === module.id
                return (
                  <button
                    key={module.id}
                    type="button"
                    onClick={() => setActiveModule(module.id)}
                    className={cn(
                      'flex flex-col items-center justify-center rounded-xl px-1 py-1.5 text-[10px] font-medium transition',
                      isActive
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="mt-0.5 truncate max-w-full">{module.label}</span>
                  </button>
                )
              })}
            </div>
            <div className="mt-2 border-t border-slate-200 pt-2 dark:border-slate-700">
              <Button variant="ghost" onClick={handleSignOut} className="h-7 w-full justify-center text-[11px] text-slate-600 dark:text-slate-300">
                <LogOut className="mr-1 h-3.5 w-3.5" /> Sign Out
              </Button>
            </div>
          </nav>

          {detailOpen && (
            <motion.aside
              initial={{ x: 320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 320, opacity: 0 }}
              className="fixed right-0 top-0 z-30 h-full w-full border-l border-slate-200 bg-white p-4 shadow-xl md:w-[340px] dark:border-slate-800 dark:bg-slate-900"
              aria-label="Detail Drawer"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Details</h2>
                <Button variant="ghost" onClick={() => setDetailOpen(false)}>Close</Button>
              </div>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{detailTitle}</p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                  <p className="text-xs text-slate-500">Next Recommended Action</p>
                  <p className="font-medium">Trigger proactive follow-up and attach supporting docs.</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                  <p className="text-xs text-slate-500">Timeline</p>
                  <p className="font-medium">Updated 5 minutes ago by workflow automation.</p>
                </div>
              </div>
            </motion.aside>
          )}
        </div>
      </div>

      {commandOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 p-4" onClick={() => {
          setCommandOpen(false)
          setCommandQuery('')
        }}>
          <div className="mx-auto mt-14 w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl dark:border-slate-700 dark:bg-slate-900" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-slate-200 pb-2 dark:border-slate-700">
              <Command className="h-4 w-4 text-slate-400" />
              <Input
                autoFocus
                value={commandQuery}
                onChange={(event) => setCommandQuery(event.target.value)}
                placeholder="Type to jump to modules"
                className="border-0 shadow-none focus-visible:ring-0"
              />
            </div>
            <div className="mt-2 max-h-72 space-y-1 overflow-auto">
              {commandItems.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setActiveModule(item.id)
                      setCommandOpen(false)
                      setCommandQuery('')
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <span className="flex items-center gap-2"><Icon className="h-4 w-4" />{item.label}</span>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {tutorialOpen && authUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/55 p-4">
          <div className="mx-auto mt-14 w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <h2 className="text-lg font-semibold">Welcome, {authUser.name.split(' ')[0]}</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Quick start tutorial for your first login.</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <p className="font-medium">1. Start in Investor OS</p>
                <p className="text-slate-500 dark:text-slate-400">Open modules from the Investor OS hub to run the full acquisition-to-rental workflow.</p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <p className="font-medium">2. Capture operations in modules</p>
                <p className="text-slate-500 dark:text-slate-400">Use Construction, Accounting, Leasing, Inbox, and Automation to update live workflow state.</p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <p className="font-medium">3. Persist profile & progress</p>
                <p className="text-slate-500 dark:text-slate-400">Your account and tutorial completion are saved with your personal workspace data on this device.</p>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTutorialOpen(false)}>Close</Button>
              <Button onClick={() => { void handleCompleteTutorial() }}>Finish Tutorial</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
